import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from 'react-i18next';
import { localizeSystemText } from '../../i18n/messages';
import { displayDate } from "../common/datePresentation";
import { OverlayTrigger, Popover } from "react-bootstrap";
import { CLIENT_LEDGER_ENTRY_TYPES, CLIENT_LEDGER_TYPE_LABELS, PAYMENT_METHOD_LABELS, formatLedgerAmount } from "./clientLedgerTypes";
import { billingYearLabel } from './ClientBillingSummary';

const isManualEntry = entry => (
  entry.type === CLIENT_LEDGER_ENTRY_TYPES.PAYMENT ||
  entry.type === CLIENT_LEDGER_ENTRY_TYPES.OPENING_BALANCE
);

function ClientLedgerList({ entries, onEdit, onDelete, onReverse, showSchoolYear }) {
  const { t: tr, i18n } = useTranslation();
  const transferLabels = { YEAR_TRANSFER_OUT: 'out', YEAR_TRANSFER_IN: 'in', YEAR_TRANSFER_REVERSAL: 'reversal' };
  return (
    <div>
      <div className="row table-header manage-table-title text-center mx-0">
        <div className="col-md-2 table-cell">{tr("manage:date_8")}</div>
        <div className="col-md-2 table-cell">{tr("manage:type_129")}</div>
        <div className="col-md-2 table-cell">{tr("manage:month_133")}</div>
        <div className="col-md-2 table-cell">{tr("manage:amount_131")}</div>
        <div className="col-md-2 table-cell">{tr("manage:method_132")}</div>
        <div className="col-md-1 table-cell">{tr("manage:note_17")}</div>
        <div className="col-md-1 table-cell">{tr("manage:actions_134")}</div>
      </div>
      {entries.map((entry, index) => (
        <div key={entry.id} className={`table-row row d-flex manage-text text-center mx-0 ${index % 2 !== 0 ? "table-row-alternate" : ""}`}>
          <div className="col-md-2 table-cell">{displayDate(entry.date, i18n.language)}</div>
          <div className="col-md-2 table-cell">{transferLabels[entry.type] ? tr(`billing:yearScope.${transferLabels[entry.type]}`) : localizeSystemText(CLIENT_LEDGER_TYPE_LABELS[entry.type], tr)}</div>
          <div className="col-md-2 table-cell">{entry.settlementMonth}{showSchoolYear && entry.schoolYearStartYear && <><br />{billingYearLabel(entry.schoolYearStartYear)}</>}</div>
          <div className="col-md-2 table-cell">{formatLedgerAmount(entry.amount, i18n.language)}</div>
          <div className="col-md-2 table-cell">{localizeSystemText(PAYMENT_METHOD_LABELS[entry.method], tr) || entry.method}</div>
          <div className="col-md-1 table-cell">
            {(entry.note || (entry.snapshot && entry.snapshot.extraFees && entry.snapshot.extraFees.length > 0) || entry.operationId) ? (
              <>
                <div className="d-none d-md-block">
                  <OverlayTrigger
                    placement="left"
                    overlay={
                      <Popover id={`popover-note-${entry.id}`} title={tr('manage:note_17')}>
                        {entry.note && <div>{entry.note}</div>}
                        {entry.snapshot && (entry.snapshot.extraFees || []).map(fee => (
                          <small key={fee.feeId} className="d-block">
                            {fee.description}: {formatLedgerAmount(fee.amountCents / 100, i18n.language)} {fee.studentNameSnapshot}
                          </small>
                        ))}
                        {entry.operationId && <small className="d-block text-break mt-1">{tr('billing:yearScope.operation')}: {entry.operationId}</small>}
                      </Popover>
                    }
                  >
                    <i className="far fa-file-alt fa-lg" style={{ cursor: "pointer" }} />
                  </OverlayTrigger>
                </div>
                <div className="d-block d-md-none">
                  {entry.note && <div>{entry.note}</div>}
                  {entry.snapshot && (entry.snapshot.extraFees || []).map(fee => (
                    <small key={fee.feeId} className="d-block">
                      {fee.description}: {formatLedgerAmount(fee.amountCents / 100, i18n.language)} {fee.studentNameSnapshot}
                    </small>
                  ))}
                  {entry.operationId && <small className="d-block text-break mt-1">{tr('billing:yearScope.operation')}: {entry.operationId}</small>}
                </div>
              </>
            ) : null}
          </div>
          <div className="col-md-1 table-cell">
            {onReverse && ['YEAR_TRANSFER_OUT', 'YEAR_TRANSFER_IN'].includes(entry.type) && !entries.some(item => item.reversesOperationId === entry.operationId) &&
              <button type="button" className="btn btn-link btn-sm" onClick={() => onReverse(entry)}>{tr('billing:yearScope.reverse')}</button>}
            {isManualEntry(entry) && (
              <>
                <button type="button" className="btn btn-link btn-sm p-0 mr-2" aria-label={`Edytuj ${entry.id}`} onClick={() => onEdit(entry)}>{tr("manage:edit_135")}</button>
                <button type="button" className="btn btn-link btn-sm p-0 text-danger" aria-label={`Usun ${entry.id}`} onClick={() => onDelete(entry)}>{tr("manage:remove_136")}</button>
              </>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

ClientLedgerList.propTypes = {
  entries: PropTypes.array.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onReverse: PropTypes.func,
  showSchoolYear: PropTypes.bool,
};

export default ClientLedgerList;
ClientLedgerList.defaultProps = { onReverse: null, showSchoolYear: true };
